<template>
    <div class="row g-3 align-items-center mb-3">
        <div class="col-auto">
            <InputField colField="mb-0" :editable="true" label="Month" :options="monthOptions" type="select"
                :modelValue="agentsUsageMonth" @change="usageMonthChange($event)" />
        </div>
        <div class="col-auto">
            <InputField label="Start Date" colField="mb-0" :modelValue="agentsStartDate" type="calendar"
                :input-config="dateConfig" @update:modelValue="startDateChange($event)" />
        </div>
        <div class="col-auto">
            <InputField label="End Date" colField="mb-0" :modelValue="agentsEndDate" type="calendar"
                :input-config="dateConfig" @update:modelValue="endDateChange($event)" />
        </div>
    </div>

    <Tabs :tabs="tabItems">

        <!-- ───── DAILY TAB ───── -->
        <template v-slot:daily>
            <div class="card">
                <div class="card-body">
                    <div v-if="agentsLoading" class="text-center py-2">
                        <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="text-muted mb-0">Loading daily data...</p>
                    </div>
                    <template v-else>
                        <div v-if="dailyRows.length === 0" class="text-center py-2">
                            <div class="text-muted mb-2">
                                <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                            </div>
                            <p class="text-muted mb-0">No data available for the selected period</p>
                        </div>
                        <div v-else>
                            <!-- Controls row -->
                            <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                                <!-- Token type toggle -->
                                <div class="d-flex align-items-center gap-2">
                                    <div class="btn-group btn-group-sm" role="group">
                                        <input type="radio" class="btn-check" id="daily-total" v-model="dailyTokenType"
                                            value="total" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="daily-total">Total</label>
                                        <input type="radio" class="btn-check" id="daily-input" v-model="dailyTokenType"
                                            value="input" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="daily-input">Input</label>
                                        <input type="radio" class="btn-check" id="daily-output" v-model="dailyTokenType"
                                            value="output" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="daily-output">Output</label>
                                    </div>
                                    <i class="fa fa-info-circle text-muted" data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        data-bs-title="Select input/output/total tokens to visualize"></i>
                                </div>

                                <div class="d-flex align-items-center gap-3">
                                    <!-- Group by agent toggle -->
                                    <div class="form-check form-switch mb-0" data-bs-theme="dark">
                                        <input :class="['form-check-input', { 'bg-secondary': !groupByAgent }]"
                                            type="checkbox" id="groupByAgent" v-model="groupByAgent">
                                        <label class="form-check-label" for="groupByAgent">Group by agent</label>
                                    </div>

                                    <!-- Window size -->
                                    <div class="d-flex align-items-center gap-2">
                                        <label class="form-label mb-0 text-muted"
                                            style="white-space:nowrap; font-size:0.85rem;">Days shown</label>
                                        <select class="form-select form-select-sm" style="width:75px;"
                                            v-model.number="dailyWindowSize">
                                            <option :value="7">7</option>
                                            <option :value="14">14</option>
                                            <option :value="30">30</option>
                                            <option :value="allDates.length">All</option>
                                        </select>
                                    </div>

                                    <!-- Prev / Next -->
                                    <div class="btn-group btn-group-sm">
                                        <button class="btn btn-outline-secondary" :disabled="dailyWindowOffset === 0"
                                            @click="dailyWindowOffset = Math.max(0, dailyWindowOffset - dailyWindowSize)">
                                            <i class="fa fa-chevron-left"></i>
                                        </button>
                                        <button class="btn btn-outline-secondary"
                                            :disabled="dailyWindowOffset + dailyWindowSize >= allDates.length"
                                            @click="dailyWindowOffset = Math.min(allDates.length - dailyWindowSize, dailyWindowOffset + dailyWindowSize)">
                                            <i class="fa fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    <span class="text-muted" style="font-size:0.8rem; white-space:nowrap;">
                                        {{ windowLabel }}
                                    </span>
                                </div>
                            </div>

                            <!-- Agent filter pills -->
                            <div class="d-flex flex-wrap gap-2 mb-3">
                                <span v-for="key in allDailyAgentKeys" :key="key" class="badge rounded-pill"
                                    :style="agentPillStyle(key)"
                                    style="cursor:pointer; user-select:none; font-size:0.8rem; padding:0.4em 0.85em;"
                                    @click="toggleDailyAgent(key)">
                                    {{ agentKeyLabel(key) }}
                                </span>
                            </div>

                            <div class="row">
                                <div class="col-sm-12 chart-view">
                                    <Bar :data="dailyChartData" :options="dailyChartOptions" />
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <!-- ───── AGGREGATE TAB ───── -->
        <template v-slot:aggregate>
            <div class="card">
                <div class="card-body">
                    <div v-if="agentsLoading" class="text-center py-2">
                        <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="text-muted mb-0">Loading aggregated data...</p>
                    </div>
                    <template v-else>
                        <div v-if="aggregateRows.length === 0" class="text-center py-2">
                            <div class="text-muted mb-2">
                                <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                            </div>
                            <p class="text-muted mb-0">No data available for the selected period</p>
                        </div>
                        <div v-else>
                            <!-- Token type + info -->
                            <div class="d-flex justify-content-between align-items-center gap-3 mb-3">
                                <div class="d-flex align-items-center gap-2">
                                    <div class="btn-group btn-group-sm" role="group">
                                        <input type="radio" class="btn-check" id="agg-total" v-model="aggTokenType"
                                            value="total" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="agg-total">Total</label>
                                        <input type="radio" class="btn-check" id="agg-input" v-model="aggTokenType"
                                            value="input" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="agg-input">Input</label>
                                        <input type="radio" class="btn-check" id="agg-output" v-model="aggTokenType"
                                            value="output" autocomplete="off">
                                        <label class="btn btn-outline-primary" for="agg-output">Output</label>
                                    </div>
                                    <i class="fa fa-info-circle text-muted" data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        data-bs-title="Select input/output/total tokens to visualize"></i>
                                </div>
                                <small class="text-muted">
                                    <i class="fa fa-info-circle"></i>
                                    Chart shows the selected token type. Table always shows all three.
                                </small>
                            </div>

                            <div class="row g-3">
                                <div class="col-lg-5 col-md-6">
                                    <div class="agg-table-wrap"
                                        :style="{ border: $store.state.darkMode ? '0.5px solid #2e3238' : '0.5px solid #dee2e6' }">
                                        <table class="table table-sm mb-0">
                                            <thead class="table-light sticky-top">
                                                <tr>
                                                    <th style="width:30%">Agent</th>
                                                    <th style="width:25%">Model</th>
                                                    <th class="text-end" style="width:15%">Input</th>
                                                    <th class="text-end" style="width:15%">Output</th>
                                                    <th class="text-end" style="width:15%">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <template v-for="group in aggregateTableData" :key="group.key">
                                                    <tr v-for="(row, i) in group.models" :key="row.model">
                                                        <td v-if="i === 0" :rowspan="group.models.length + 1"
                                                            class="fw-semibold align-middle"
                                                            style="max-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                                                            <span :title="agentKeyLabel(group.key)">{{ agentKeyLabel(group.key) }}</span>
                                                        </td>
                                                        <td
                                                            style="max-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; padding-right:4px;">
                                                            <span class="badge text-white"
                                                                :style="`background:${modelColor(row.model)}; font-size:0.7rem; font-weight:400; max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;`"
                                                                :title="row.model">{{ modelLabel(row.model) }}</span>
                                                        </td>
                                                        <td class="text-end">{{ formatNumber(row.inputToken) }}</td>
                                                        <td class="text-end">{{ formatNumber(row.outputToken) }}</td>
                                                        <td class="text-end fw-semibold">{{ formatNumber(row.totalToken)
                                                            }}
                                                        </td>
                                                    </tr>
                                                    <!-- subtotal row -->
                                                    <tr class="subtotal-row">
                                                        <td class="fst-italic text-muted" style="font-size:0.8rem;">
                                                            subtotal
                                                        </td>
                                                        <td class="text-end text-muted" style="font-size:0.8rem;">{{
                                                            formatNumber(group.totalInput) }}</td>
                                                        <td class="text-end text-muted" style="font-size:0.8rem;">{{
                                                            formatNumber(group.totalOutput) }}</td>
                                                        <td class="text-end fw-bold" style="font-size:0.8rem;">{{
                                                            formatNumber(group.totalTokens) }}</td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Chart -->
                                <div class="col-lg-7 col-md-6">
                                    <!-- Dynamic height: 52px per agent row, min 300px -->
                                    <div :style="`position:relative; height:${aggChartHeight}px;`">
                                        <Bar :data="aggregateChartData" :options="aggregateChartOptions" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </Tabs>
</template>

<script>
import InputField from '../../components/InputField.vue';
import { generateLastSixMonthOptions, getFirstDayOfMonth, getLastDayOfMonth, formatNumber } from '../../utils/commonFunction';
import Tabs from "../../components/Tabs.vue";
import { mapState } from "vuex";
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS, Title, Tooltip, Legend,
    BarElement, CategoryScale, LinearScale, Colors
} from 'chart.js';
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors);

// Key: substring or full model ID to match (checked with .includes())
// Value: display shorthand
// Add new entries here as new models are onboarded.
// If no entry matches, the raw model ID is used as-is.
const MODEL_SHORTHAND = {
    'claude-haiku-4-5': 'Haiku 4.5',
    'claude-haiku-3': 'Haiku 3',
    'claude-sonnet-4-5': 'Sonnet 4.5',
    'claude-sonnet-4-6': 'Sonnet 4.6',
    'claude-sonnet-3-5': 'Sonnet 3.5',
    'claude-opus-4': 'Opus 4',
    'claude-opus-3': 'Opus 3',
    'gpt-4o': 'GPT-4o',
    'gpt-4-turbo': 'GPT-4 Turbo',
    'gpt-3.5-turbo': 'GPT-3.5 Turbo',
    'gemini-1.5-pro': 'Gemini 1.5 Pro',
    'gemini-1.5-flash': 'Gemini 1.5 Flash',
    'mistral-large': 'Mistral Large',
    'llama-3': 'Llama 3',
};
function modelLabel(modelId) {
    if (!modelId) return modelId;
    for (const [key, label] of Object.entries(MODEL_SHORTHAND)) {
        if (modelId.includes(key)) return label;
    }
    return modelId; // fallback: raw ID
}

// Backend "key" values are the storage feature namespaces (kept stable so
// existing chats/metrics don't get orphaned). Map them to user-facing labels.
const AGENT_KEY_LABEL = {
    'aquabot': 'PAICE',
};
function agentKeyLabel(key) {
    if (!key) return key;
    return AGENT_KEY_LABEL[key] || key;
}
// These are used for model-level coloring in both daily (model×agent combos)
// and aggregate charts. Fully opaque, perceptually distinct.
const MODEL_COLORS = [
    '#4e79a7', // steel blue
    '#e15759', // coral red
    '#f28e2b', // orange
    '#59a14f', // green
    '#b07aa1', // purple
    '#76b7b2', // teal
    '#ff9da7', // pink
    '#9c755f', // brown
    '#edc948', // yellow  ← still here but only used if >8 models; not as opacity overlay
    '#bab0ac', // gray
];
// Per-agent colors (used for agent pills + grouped-by-agent daily bars)
const AGENT_COLORS = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2',
    '#59a14f', '#edc948', '#b07aa1', '#ff9da7',
    '#9c755f', '#bab0ac',
];
function compactTick(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return v;
}
function tokenField(type) {
    return { input: 'inputToken', output: 'outputToken', total: 'totalToken' }[type] ?? 'totalToken';
}
export default {
    name: 'AiUsageAgents',
    components: { InputField, Tabs, Bar },
    props: ['agentsLoading'],
    emits: ['getUsageData'],
    data() {
        return {
            dateConfig: { wrap: true, dateFormat: 'Y-m-d' },
            monthOptions: generateLastSixMonthOptions(),

            dailyTokenType: 'total',
            groupByAgent: false,
            dailyWindowSize: 14,
            dailyWindowOffset: 0,
            selectedDailyAgents: [],

            aggTokenType: 'total',
        };
    },
    computed: {
        ...mapState({
            agentsStartDate: state => state.admin.agentsUsageDataStartDate,
            agentsEndDate: state => state.admin.agentsUsageDataEndDate,
            agentsUsageMonth: state => state.admin.agentsUsageMonth,
            agentsDataDaily: state => state.admin.agentsUsageData.daily ?? null,
            agentsDataAggregate: state => state.admin.agentsUsageData.aggregate ?? null,
        }),

        tabItems() {
            return [
                { name: "Daily", id: "id1", slot: "daily", loading: this.agentsLoading },
                { name: "Aggregate", id: "id2", slot: "aggregate", loading: this.agentsLoading },
            ];
        },

        // ── Daily ──────────────────────────────────────────────────────────────
        dailyRows() {
            if (!this.agentsDataDaily?.data) return [];
            const { headers, data } = this.agentsDataDaily;
            const idx = f => headers.indexOf(f);
            return data.map(row => ({
                date: row[idx('date')],
                key: row[idx('key')],
                model: row[idx('model')],
                inputToken: row[idx('input_token')] ?? 0,
                outputToken: row[idx('output_token')] ?? 0,
                totalToken: row[idx('total_token')] ?? 0,
            }));
        },
        allDailyAgentKeys() {
            return [...new Set(this.dailyRows.map(r => r.key))].sort();
        },
        allDates() {
            return [...new Set(this.dailyRows.map(r => r.date))].sort();
        },
        windowedDates() {
            return this.allDates.slice(
                this.dailyWindowOffset,
                this.dailyWindowOffset + this.dailyWindowSize
            );
        },
        windowLabel() {
            const d = this.windowedDates;
            if (!d.length) return '';
            return d.length === 1 ? d[0] : `${d[0]} – ${d[d.length - 1]}`;
        },
        agentColorMap() {
            const map = {};
            this.allDailyAgentKeys.forEach((k, i) => {
                map[k] = AGENT_COLORS[i % AGENT_COLORS.length];
            });
            return map;
        },
        modelColorMap() {
            // Derive all unique models from both daily and aggregate so colors
            // stay consistent across both tabs
            const dailyModels = this.dailyRows.map(r => r.model);
            const aggModels = this.aggregateRows.map(r => r.model);
            const allModels = [...new Set([...dailyModels, ...aggModels])].sort();
            const map = {};
            allModels.forEach((m, i) => {
                map[m] = MODEL_COLORS[i % MODEL_COLORS.length];
            });
            return map;
        },
        dailyChartData() {
            const field = tokenField(this.dailyTokenType);
            const dates = this.windowedDates;
            const activeKeys = this.selectedDailyAgents;
            const rows = this.dailyRows.filter(r =>
                activeKeys.includes(r.key) && dates.includes(r.date)
            );
            if (this.groupByAgent) {
                // One dataset per agent key — models summed together
                const datasets = activeKeys.map(key => {
                    const color = this.agentColorMap[key] ?? '#888';
                    const dateValues = {};
                    rows.filter(r => r.key === key).forEach(r => {
                        dateValues[r.date] = (dateValues[r.date] ?? 0) + r[field];
                    });
                    return {
                        label: agentKeyLabel(key),
                        data: dates.map(d => dateValues[d] ?? 0),
                        backgroundColor: color + 'cc',
                        borderColor: '#ffffff00',
                        borderWidth: {
                            top: 2,
                            right: 0,
                            bottom: 0,
                            left: 0
                        },
                        stack: 'grouped',
                    };
                });
                return { labels: dates, datasets };
            } else {
                // One dataset per (agent × model) — all unique model IDs from data
                const allModels = [...new Set(rows.map(r => r.model))].sort();
                const datasets = [];

                for (const key of activeKeys) {
                    const agentColor = this.agentColorMap[key] ?? '#888';
                    // Spread model opacity across the model count to stay within the agent hue
                    allModels.forEach((model, mi) => {
                        const opacity = Math.round(180 + (mi / Math.max(allModels.length - 1, 1)) * 70)
                            .toString(16).padStart(2, '0');
                        const dateValues = {};
                        rows.filter(r => r.key === key && r.model === model).forEach(r => {
                            dateValues[r.date] = (dateValues[r.date] ?? 0) + r[field];
                        });
                        const hasData = Object.values(dateValues).some(v => v > 0);
                        if (!hasData) return; // skip combos with no data in this window
                        datasets.push({
                            label: `${agentKeyLabel(key)} · ${modelLabel(model)}`,
                            data: dates.map(d => dateValues[d] ?? 0),
                            backgroundColor: agentColor + opacity,
                            borderColor: '#ffffff00',
                            borderWidth: {
                                top: 2,
                                right: 0,
                                bottom: 0,
                                left: 0
                            },
                            stack: key,
                        });
                    });
                }
                return { labels: dates, datasets };
            }
        },
        dailyChartOptions() {
            const tokenLabel = {
                total: 'Total Tokens', input: 'Input Tokens', output: 'Output Tokens'
            }[this.dailyTokenType];

            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';

            return {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: {
                        position: 'bottom',
                        labels: { font: { size: 11 }, boxWidth: 12, padding: 10, color: textColor },
                    },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} tokens`,
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        title: { display: true, text: 'Date' },
                        ticks: { maxRotation: 45, autoSkip: false },
                        grid: { color: gridColor, drawOnChartArea: true } // Styles X grid lines
                    },
                    y: {
                        stacked: true,
                        title: { display: true, text: tokenLabel },
                        ticks: { callback: compactTick },
                        grid: { color: gridColor, drawOnChartArea: true } // Styles X grid lines
                    },
                },
            };
        },

        // ── Aggregate ──────────────────────────────────────────────────────────
        aggregateRows() {
            if (!this.agentsDataAggregate?.data) return [];
            const { headers, data } = this.agentsDataAggregate;
            const idx = f => headers.indexOf(f);
            return data.map(row => ({
                key: row[idx('key')],
                model: row[idx('model')],
                inputToken: row[idx('input_token')] ?? 0,
                outputToken: row[idx('output_token')] ?? 0,
                totalToken: row[idx('total_token')] ?? 0,
            }));
        },
        aggregateTableData() {
            const groups = {};
            for (const r of this.aggregateRows) {
                if (!groups[r.key]) {
                    groups[r.key] = { key: r.key, models: [], totalInput: 0, totalOutput: 0, totalTokens: 0 };
                }
                groups[r.key].models.push(r);
                groups[r.key].totalInput += r.inputToken;
                groups[r.key].totalOutput += r.outputToken;
                groups[r.key].totalTokens += r.totalToken;
            }
            return Object.values(groups).sort((a, b) => b.totalTokens - a.totalTokens);
        },
        aggChartHeight() {
            return Math.max(300, this.aggregateTableData.length * 52);
        },
        aggregateChartData() {
            const field = tokenField(this.aggTokenType);
            const rows = this.aggregateRows;
            const agentKeys = [...new Set(rows.map(r => r.key))].sort();
            const allModels = [...new Set(rows.map(r => r.model))].sort();

            const datasets = allModels.map(model => {
                const color = this.modelColorMap[model] ?? '#888';
                return {
                    label: modelLabel(model),
                    data: agentKeys.map(key => {
                        const match = rows.find(r => r.key === key && r.model === model);
                        return match ? match[field] : 0;
                    }),
                    backgroundColor: color,
                    borderColor: '#ffffff00',
                    borderWidth: 1,
                };
            });

            return { labels: agentKeys, datasets };
        },
        aggregateChartOptions() {
            const tokenLabel = {
                total: 'Total Tokens', input: 'Input Tokens', output: 'Output Tokens'
            }[this.aggTokenType];
            
            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';

            return {
                indexAxis: 'y',
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: {
                        position: 'bottom',
                        labels: { font: { size: 11 }, boxWidth: 12, padding: 10, color: textColor },
                    },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.x.toLocaleString()} tokens`,
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        title: { display: true, text: tokenLabel },
                        ticks: { callback: compactTick },
                        grid: { color: gridColor, drawOnChartArea: true } // Styles X grid lines
                    },
                    y: { 
                        stacked: true,
                        grid: { color: gridColor, drawOnChartArea: true } // Styles Y grid lines
                    },
                },
            };
        },
    },
    watch: {
        allDailyAgentKeys: {
            immediate: true,
            handler(keys) {
                const existing = new Set(this.selectedDailyAgents);
                if (!existing.size) {
                    this.selectedDailyAgents = [...keys];
                } else {
                    this.selectedDailyAgents = keys.filter(k => existing.has(k));
                    const added = keys.filter(k => !existing.has(k));
                    this.selectedDailyAgents.push(...added);
                }
            },
        },
        allDates(newDates) {
            this.dailyWindowOffset = Math.max(0, newDates.length - this.dailyWindowSize);
        },
        dailyWindowSize() {
            this.dailyWindowOffset = Math.min(
                this.dailyWindowOffset,
                Math.max(0, this.allDates.length - this.dailyWindowSize)
            );
        },
    },
    methods: {
        formatNumber,
        modelLabel,
        agentKeyLabel,
        modelColor(modelId) {
            return this.modelColorMap[modelId] ?? '#6c757d';
        },
        toggleDailyAgent(key) {
            const idx = this.selectedDailyAgents.indexOf(key);
            if (idx === -1) {
                this.selectedDailyAgents.push(key);
            } else if (this.selectedDailyAgents.length > 1) {
                this.selectedDailyAgents.splice(idx, 1);
            }
        },
        agentPillStyle(key) {
            const color = this.agentColorMap[key] ?? '#888';
            const active = this.selectedDailyAgents.includes(key);
            return active
                ? `background:${color}; color:#fff; border:1.5px solid ${color};`
                : `background:transparent; color:${color}; border:1.5px solid ${color};`;
        },
        async usageMonthChange(month) {
            this.$store.dispatch('admin/setAgentsUsageMonth', month);
            this.$store.dispatch('admin/setAgentsUsageDataStartDate', getFirstDayOfMonth(month));
            this.$store.dispatch('admin/setAgentsUsageDataEndDate', getLastDayOfMonth(month));
            this.$emit('getUsageData', true)
        },
        async startDateChange(newValue) {
            if (newValue !== this.agentsStartDate) {
                this.$store.dispatch('admin/setAgentsUsageMonth', 'custom');
                this.$store.dispatch('admin/setAgentsUsageDataStartDate', newValue);
                this.$emit('getUsageData', true)
            }
        },
        async endDateChange(newValue) {
            if (newValue !== this.agentsEndDate) {
                this.$store.dispatch('admin/setAgentsUsageMonth', 'custom');
                this.$store.dispatch('admin/setAgentsUsageDataEndDate', newValue);
                this.$emit('getUsageData', true)
            }
        },
    },
    beforeMount() {
        this.$emit('getUsageData', false)
    },
};
</script>
<style scoped>
.chart-view {
    min-height: 380px;
    position: relative;
}

.agg-table-wrap {
    max-height: 520px;
    overflow-y: auto;
    border-radius: 4px;
}

.agg-table-wrap table {
    table-layout: fixed;
    width: 100%;
}

/* Subtotal rows visually separated */
.subtotal-row td {
    background: #f8f9fa;
    border-top: 1px dashed #dee2e6;
}
</style>